/**
 * StackedStringList — editable view for string_list variables.
 *
 * #10 (reframed): only rendered when the parent subgroup is EXPANDED (editable).
 * Shows stacked Input rows, each with a remove (X) button.
 * Collapsed/summary view renders MDV-style tags directly in VariableInput.
 *
 * "Add Another" button present in expanded view; sized to match collapsed input height.
 */
import React, { useRef } from "react";
import { v4 as uuid } from "uuid";
import styled from "styled-components";
import Input from "@cs/components/src/Input";
import Button from "@cs/components/src/Button";
import { Plus, X } from "react-feather";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  border: 1px solid var(--dark500);
  border-radius: 10px;
  padding: 5px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const InputWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const RemoveSlot = styled.div`
  flex-shrink: 0;
  width: 30px;
`;

// "Add Another" sized to match the collapsed-input row height (30px).
const AddRow = styled.div`
  display: flex;
  align-items: center;
`;

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  required?: boolean;
  /** When true, show editable inputs with remove X + Add Another button.
   *  Always true since StackedStringList is only rendered in expanded view. */
  isExpanded?: boolean;
}

const StackedStringList = ({
  value,
  onChange,
  required,
  isExpanded = true,
}: Props) => {
  const safeValue = Array.isArray(value) ? value : [];

  // Stable keys per entry — prevents React from reusing DOM nodes on mid-list delete.
  const stableKeys = useRef<string[]>([]);
  while (stableKeys.current.length < safeValue.length) {
    stableKeys.current.push(uuid());
  }

  const updateAt = (index: number, newVal: string) => {
    const next = safeValue.map((v, i) => (i === index ? newVal : v));
    onChange(next);
  };

  const removeAt = (index: number) => {
    stableKeys.current.splice(index, 1);
    onChange(safeValue.filter((_, i) => i !== index));
  };

  const add = () => {
    stableKeys.current.push(uuid());
    onChange([...safeValue, ""]);
  };

  return (
    <Wrapper>
      {safeValue.map((entry, index) => (
        <Row key={stableKeys.current[index]}>
          <InputWrapper>
            <Input
              value={entry}
              onChange={(v) => updateAt(index, v)}
              required={required && index === 0 && safeValue.length === 1}
              buttonSize={26}
              minWidth={50}
              height={30}
            />
          </InputWrapper>
          {/* #10: X remove shown in expanded/editable view */}
          <RemoveSlot>
            <Button
              icon={X}
              type="danger"
              borderless
              size={30}
              tooltip="Remove"
              onClick={() => removeAt(index)}
            />
          </RemoveSlot>
        </Row>
      ))}
      {/* Add Another: shown in expanded view, sized to match input row height */}
      {isExpanded && (
        <AddRow>
          <Button
            icon={Plus}
            label="Add Another"
            type="default"
            borderless
            size={25}
            onClick={add}
          />
        </AddRow>
      )}
    </Wrapper>
  );
};

export default StackedStringList;
